/**
 * The last crumb on a resource bar — the page's own name — in its three states:
 * plain text, a hover target, and an editor.
 *
 * Split out of `ResourcePageShell` because none of it is about the shell. The
 * shell resolves folder ancestry, collapses a deep trail, portals actions into
 * a panel bar; this draws one name and lets you retype it, and it is the half
 * that has states worth testing on their own.
 */
import React from 'react';
import { cn, FOCUS_RING } from '@cotera/client/v0/lib/utils';

/**
 * The slice of a breadcrumb this module draws.
 *
 * Deliberately not `BreadcrumbItem`. Nothing here navigates, resolves a folder
 * or opens an artifact — it renders a name and lets you retype it — and taking
 * the whole model would have bound this file to the shell that owns it, in both
 * directions. A structural type keeps the dependency one-way: `BreadcrumbItem`
 * satisfies this, and this knows nothing about `BreadcrumbItem`.
 */
export type CrumbLabelParts = {
  label: string;
  /** Optional emoji shown before the label. */
  emoji?: string | null;
};

export const CrumbLabel: React.FC<CrumbLabelParts> = ({ emoji, label }) => (
  <>
    {emoji !== undefined && emoji !== null ? (
      <span className="text-base leading-none shrink-0">{emoji}</span>
    ) : null}
    {/* Every crumb truncates — folder names at 180px, the page's own title
        against whatever the actions leave it. A clipped name needs a way to be
        read. */}
    <span className="truncate" title={label}>
      {label}
    </span>
  </>
);

/**
 * `min-w-0 flex-1` rather than a fixed cap: the title is the one crumb worth
 * spending leftover bar on, and the viewport-relative cap it used to carry
 * ignored both the sidebar in front of it and the actions beside it, so titles
 * ellipsized with room to spare.
 */
const CURRENT_CRUMB_TEXT = 'text-sm font-medium text-foreground';
const CURRENT_CRUMB_LAYOUT =
  'inline-flex min-w-0 items-center gap-1 truncate px-1';
/**
 * Padding for the two states that draw a box around the title — the hover fill
 * and the editor.
 *
 * One value, because they are the same words in the same place and any
 * disagreement is a jump under the cursor: the button carried `px-1` against
 * the input's `px-1.5`, so clicking the title nudged it 2px sideways. Vertically
 * the button had none at all, which left the hover fill clamped to the exact
 * line box — a tint on the text rather than a target around it.
 *
 * `py-1`/`px-2` is the next stop on the scale in both directions. The input's
 * border sits outside this, so its text lands 1px in from the button's; the two
 * are never on screen together.
 */
const CURRENT_CRUMB_BOX_PADDING = 'px-2 py-1';
/**
 * `flex-1` is for the plain, un-paintable crumb only. It costs nothing on a
 * bare span, and it is what keeps a long title using the leftover bar. On the
 * editable one it would stretch the hover fill the width of the pane, which
 * says the whole bar is the click target rather than the name. Dropping it
 * there does not cost truncation: `min-w-0` plus `truncate` still ellipsize a
 * title too long for the space, they just stop at the text when it fits.
 */
const CURRENT_CRUMB_CLASS = cn(
  CURRENT_CRUMB_LAYOUT,
  'flex-1',
  CURRENT_CRUMB_TEXT
);

/**
 * The page's own name, edited where it is written.
 *
 * Renaming used to be reachable only from a `⋯` menu, three clicks and a modal
 * away from a title sitting in plain sight — and on a chat that title is
 * model-generated, so it is the field most likely to want correcting. Clicking
 * it is what every comparable editor does.
 *
 * A page that offers this should not also keep a Rename row in its overflow
 * menu: the row opened a dialog, which is a second interaction model for one
 * operation. Surfaces with no bar of their own — list rows, any generic action
 * runner — still reach the rename action directly, and the dialog is theirs.
 */
const EditableCurrentCrumb: React.FC<{
  crumb: CrumbLabelParts;
  onRename: (name: string) => void;
}> = ({ crumb, onRename }) => {
  const [draft, setDraft] = React.useState<string | null>(null);
  // Set whenever this component closes the editor itself, so the blur that
  // follows unmounting the input is recognised as its own and ignored. Without
  // it, Enter commits and then the blur commits the same stale draft again, and
  // Escape discards and then the blur saves what it just discarded.
  const closedItselfRef = React.useRef(false);

  // A rename that lands from anywhere else — the `⋯` dialog, another tab, the
  // model retitling the chat — must not sit behind a stale draft.
  React.useEffect(() => {
    setDraft(null);
  }, [crumb.label]);

  const close = (commit: boolean): void => {
    const next = draft?.trim() ?? '';
    closedItselfRef.current = true;
    setDraft(null);
    // An empty name is a cancel, not a rename to nothing; an unchanged one is
    // not worth a request or a toast.
    if (!commit || next.length === 0 || next === crumb.label) {
      return;
    }
    onRename(next);
  };

  if (draft === null) {
    return (
      <button
        type="button"
        onClick={() => {
          closedItselfRef.current = false;
          setDraft(crumb.label);
        }}
        title={`Rename “${crumb.label}”`}
        className={cn(
          CURRENT_CRUMB_LAYOUT,
          CURRENT_CRUMB_TEXT,
          CURRENT_CRUMB_BOX_PADDING,
          // `sidebar-accent`, this app's designated hover — the back button a
          // few pixels to the left of this title already uses it, so the two
          // controls in this bar now light up the same way.
          //
          // `bg-muted` could not be seen. The v1 theme never redefines
          // `--muted`, so it falls through to the base `oklch(0.967 0.001
          // 286.375)`: nine tenths of a point of lightness below this bar's own
          // `oklch(0.9761 …)`, and cool where everything around it is warm — a
          // hover you had to look for, in the wrong temperature. `--sidebar-
          // accent` is `oklch(0.947 0.0074 80.72)`, about three times the step
          // down and in the palette's own hue family.
          'max-w-full cursor-text rounded-md text-left transition-colors hover:bg-sidebar-accent',
          FOCUS_RING
        )}
      >
        <CrumbLabel emoji={crumb.emoji} label={crumb.label} />
      </button>
    );
  }

  return (
    <input
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      value={draft}
      aria-label="Name"
      // `field-sizing: content` is what makes the box stop at the end of the
      // name instead of running the width of the bar — a full-width field over
      // a short title reads as a search box, not as the title being edited.
      // `size` is the fallback where that is unsupported (Safari, Firefox): an
      // approximate character count, which lands close enough for a name.
      size={Math.min(Math.max(draft.length + 1, 12), 60)}
      onFocus={(event) => event.currentTarget.select()}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (closedItselfRef.current) {
          closedItselfRef.current = false;
          return;
        }
        close(true);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          close(true);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          close(false);
        }
      }}
      className={cn(
        CURRENT_CRUMB_TEXT,
        // The app's own field chrome, from `Input`: a border on the page's own
        // ground, not a filled grey block. A shaded box reads as disabled or as
        // a placeholder; an outlined one reads as "this is yours to type in",
        // which is the whole message an editor open on a title has to send.
        //
        // Deliberately without the app's `FOCUS_RING`. That ring is 3px of
        // `--ring` at half alpha, and `--ring` is a warm hue — around a small
        // white field it did not read as focus, it read as a beige smear behind
        // the border. Nothing is lost: this input exists *only* while focused,
        // so a focus-versus-rest distinction has no second state to draw, and
        // the border and caret already say where the keyboard is.
        //
        // `bg-card`, not `bg-background`: the two differ by the warm cast the
        // whole surface carries (`--background` is `oklch(0.9761 0.0041 91.45)`,
        // chroma at the yellow end of the wheel), and picking the field out of
        // the bar it sits in is the entire job of the fill. `--card` is the
        // app's white — chroma 0, `oklch(0.997)`, a rounding error off #fff and
        // the only one of the two that is still right in the dark theme.
        CURRENT_CRUMB_BOX_PADDING,
        'field-sizing-content max-w-full min-w-0 rounded-md border border-input bg-card outline-none'
      )}
    />
  );
};

/** The current page's own crumb, editable in place when the page allows it. */
export const CurrentCrumb: React.FC<{
  crumb: CrumbLabelParts;
  onRename?: (name: string) => void;
}> = ({ crumb, onRename }) =>
  onRename !== undefined ? (
    <EditableCurrentCrumb crumb={crumb} onRename={onRename} />
  ) : (
    <span className={CURRENT_CRUMB_CLASS}>
      <CrumbLabel emoji={crumb.emoji} label={crumb.label} />
    </span>
  );
